import type { IconDefinition } from '@fortawesome/pro-solid-svg-icons'
import {
  faBallotCheck,
  faBarcode,
  faBells,
  faBox,
  faBoxCheck,
  faBoxes,
  faBroom,
  faBuilding,
  faBuildingFlag,
  faBusinessTime,
  faCameraCctv,
  faChartLine,
  faCircleNodes,
  faCity,
  faClipboardList,
  faCompassDrafting,
  faDiagramProject,
  faEnvelopeOpenText,
  faEthernet,
  faExchange,
  faFileContract,
  faFileExport,
  faFileInvoice,
  faFolderOpen,
  faGaugeHigh,
  faGears,
  faGlobe,
  faHistory,
  faHouse,
  faHouseCircleXmark,
  faKey,
  faLandmark,
  faLayerGroup,
  faLink,
  faListCheck,
  faLock,
  faMagnifyingGlassLocation,
  faMapLocationDot,
  faMicrochip,
  faMonitorWaveform,
  faNetworkWired,
  faPhoneOffice,
  faPlugCircleCheck,
  faPlugCircleXmark,
  faReceipt,
  faRectangleList,
  faRouter,
  faScannerGun,
  faScrewdriverWrench,
  faServer,
  faShieldCheck,
  faShieldHalved,
  faShieldVirus,
  faShovel,
  faSignsPost,
  faSiren,
  faSitemap,
  faSliders,
  faSquarePlus,
  faTableColumns,
  faTags,
  faTowerBroadcast,
  faTrademark,
  faTrianglePersonDigging,
  faTruck,
  faTruckMoving,
  faUser,
  faUserGroup,
  faUserLock,
  faUserMagnifyingGlass,
  faUserTie,
  faWarehouse,
  faWifi,
  faWrench,
} from '@fortawesome/pro-solid-svg-icons'

/*
 * The sidebar, its search field and the browser tab title all read this one tree. Adding a page means adding a
 * link to the section or group it belongs to; there is no third level. A group's `permission` gates every link in
 * it, and a link also needs its own node (read). Every icon appears once, so the icon-only sidebar and the search
 * results stay readable — menu.test.ts enforces it.
 */

export interface MenuLink {
  label: string
  path: string
  icon: IconDefinition
  permission?: string
  /** Search words that are in neither the label nor the group's label. */
  keywords?: string[]
}

export interface MenuGroup {
  label: string
  icon: IconDefinition
  permission?: string
  links: MenuLink[]
}

export interface MenuSection {
  label?: string
  items: Array<MenuLink | MenuGroup>
}

/** A link together with the group it sits in, if any. */
export interface MenuHit {
  link: MenuLink
  group?: MenuGroup
}

export const MENU: MenuSection[] = [
  {
    items: [
      { label: 'Dashboard', path: '/', icon: faTableColumns },
    ],
  },
  {
    label: 'Netwerk',
    items: [
      {
        label: 'Netwerkbeheer',
        icon: faNetworkWired,
        permission: '/frontend/modules/netwerkbeheer',
        links: [
          { label: 'Aansluitingen', path: '/netwerkbeheer/aansluitingen', icon: faBuildingFlag, permission: '/frontend/modules/netwerkbeheer/aansluitingen' },
          { label: 'Interconnects', path: '/netwerkbeheer/interconnects', icon: faCircleNodes, permission: '/frontend/modules/netwerkbeheer/interconnects' },
          { label: 'EVC\'s', path: '/netwerkbeheer/evcs', icon: faEthernet, permission: '/frontend/modules/netwerkbeheer/evcs' },
          // Frontend node on purpose: provider groups DENY it to hide the inventory while keeping their own devices.
          { label: 'Apparaten', path: '/netwerkbeheer/apparaten', icon: faRouter, permission: '/frontend/modules/netwerkbeheer/apparaten', keywords: ['devices', 'switch', 'cpe', 'modem'] },
          { label: 'POP\'s', path: '/netwerkbeheer/pops', icon: faServer, permission: '/modules/netwerkbeheer/pops', keywords: ['rack'] },
          // The model catalog belongs to the inventory: its API node (devices) is one provider groups hold, so it shares Apparaten's.
          { label: 'Apparaatmodellen', path: '/netwerkbeheer/apparaatmodellen', icon: faMicrochip, permission: '/frontend/modules/netwerkbeheer/apparaten' },
          { label: 'IP Adressen', path: '/netwerkbeheer/ip', icon: faSitemap, permission: '/modules/netwerkbeheer/ip', keywords: ['ipam', 'subnet', 'blokken'] },
          { label: 'Workflows', path: '/workflows', icon: faDiagramProject, permission: '/modules/connections/workflow/steps' },
        ],
      },
      {
        label: 'Provisioning',
        icon: faGears,
        permission: '/frontend/modules/provisioning',
        links: [
          { label: 'Routers', path: '/provisioning/routers', icon: faWifi },
          { label: 'Apparatuurcodes', path: '/provisioning/apparatuurcodes', icon: faBarcode, keywords: ['sticker'] },
          { label: 'Voorbereide apparaten', path: '/provisioning/voorbereide-apparaten', icon: faBoxCheck },
        ],
      },
      {
        label: 'RADIUS',
        icon: faShieldCheck,
        permission: '/modules/radius',
        links: [
          { label: 'Dashboard', path: '/radius/dashboard', icon: faGaugeHigh, permission: '/modules/radius/dashboard' },
          { label: 'User Lookup', path: '/radius/user-lookup', icon: faUserMagnifyingGlass, keywords: ['pppoe', 'gebruiker'] },
          { label: 'Active Sessions', path: '/radius/sessions/active', icon: faPlugCircleCheck, permission: '/modules/radius/accounting', keywords: ['pppoe', 'sessies'] },
          { label: 'Session History', path: '/radius/sessions/history', icon: faHistory, permission: '/modules/radius/accounting', keywords: ['sessies'] },
          { label: 'NAS Management', path: '/radius/nas', icon: faTowerBroadcast, permission: '/modules/radius/nas' },
          { label: 'Authentication Logs', path: '/radius/auth-logs', icon: faLock, permission: '/modules/radius/postauth' },
          { label: 'Reports', path: '/radius/reports', icon: faClipboardList, keywords: ['rapportages'] },
          { label: 'Orphaned Records', path: '/radius/maintenance/orphaned', icon: faBroom, permission: '/modules/radius/maintenance', keywords: ['opschonen'] },
        ],
      },
    ],
  },
  {
    label: 'Klanten & verkoop',
    items: [
      { label: 'Klanten', path: '/customers', icon: faUserTie, permission: '/frontend/modules/klanten' },
      {
        label: 'Projecten',
        icon: faCompassDrafting,
        permission: '/frontend/modules/projecten',
        links: [
          { label: 'Alle Projecten', path: '/projecten', icon: faFolderOpen },
          { label: 'Proposities', path: '/propositions', icon: faFileContract, permission: '/modules/projects/propositions', keywords: ['toewijzingen'] },
        ],
      },
      {
        label: 'BAG',
        icon: faSignsPost,
        permission: '/frontend/modules/bag',
        links: [
          { label: 'Alle Adressen', path: '/bag/adressen', icon: faHouse },
          { label: 'BU Finder', path: '/bag/bufinder', icon: faMagnifyingGlassLocation, permission: '/frontend/modules/bag/bufinder' },
          { label: 'Ingetrokken Adressen', path: '/bag/revoked', icon: faHouseCircleXmark, permission: '/modules/netwerkbeheer/aansluitingen/revoked-bag' },
        ],
      },
    ],
  },
  {
    // What a customer gets on top of the line, each drilling down into its own configuration.
    label: 'Diensten',
    items: [
      { label: 'VoIP', path: '/voip/klanten', icon: faPhoneOffice, permission: '/frontend/modules/voip/klanten', keywords: ['voys', 'telefonie'] },
      {
        label: 'Beveiliging',
        icon: faShieldHalved,
        permission: '/frontend/modules/beveiliging',
        links: [
          { label: 'Camerasystemen', path: '/beveiliging/camerasystemen', icon: faCameraCctv, permission: '/frontend/modules/beveiliging/camerasystemen' },
          { label: 'Alarmsystemen', path: '/beveiliging/alarmsystemen', icon: faSiren, permission: '/frontend/modules/beveiliging/alarmsystemen' },
        ],
      },
    ],
  },
  {
    label: 'Storingen & meldingen',
    items: [
      { label: 'Storingsdashboard', path: '/monitoring/storingen', icon: faMonitorWaveform, permission: '/frontend/modules/monitoring/issues', keywords: ['monitoring', 'zabbix'] },
      {
        label: 'Meldingen',
        icon: faBells,
        permission: '/frontend/modules/meldingen',
        links: [
          { label: 'Onderhoud', path: '/meldingen/onderhoud', icon: faWrench, permission: '/modules/meldingen/onderhoud' },
          { label: 'Storingen', path: '/meldingen/storingen', icon: faPlugCircleXmark, permission: '/modules/meldingen/storing' },
          { label: 'Cyberdreigingen', path: '/meldingen/cyberdreigingen', icon: faShieldVirus, permission: '/modules/meldingen/cyberdreigingen' },
        ],
      },
    ],
  },
  {
    label: 'Infrastructuur',
    items: [
      { label: 'Kaart', path: '/kaart', icon: faMapLocationDot, permission: '/frontend/modules/kaart' },
      { label: 'Grondwerk', path: '/grondwerk', icon: faTrianglePersonDigging, permission: '/frontend/modules/grondwerk', keywords: ['netten', 'kabels', 'imkl'] },
      { label: 'Graafmeldingen', path: '/klic/graafmeldingen', icon: faShovel, permission: '/frontend/modules/klic/graafmeldingen', keywords: ['klic', 'kadaster'] },
    ],
  },
  {
    label: 'Bedrijfsvoering',
    items: [
      {
        label: 'Voorraad',
        icon: faBoxes,
        permission: '/frontend/modules/inventory',
        links: [
          { label: 'Warenhuis', path: '/voorraad/warenhuizen', icon: faWarehouse, permission: '/frontend/modules/inventory/warehouse' },
          { label: 'Merken', path: '/voorraad/merken', icon: faTrademark, permission: '/frontend/modules/inventory/brands' },
          { label: 'Leveranciers', path: '/voorraad/leveranciers', icon: faTruck, permission: '/frontend/modules/inventory/vendors' },
          { label: 'Producten', path: '/voorraad/producten', icon: faBox, permission: '/frontend/modules/inventory/products' },
          { label: 'Stock Dashboards', path: '/voorraad/dashboards', icon: faChartLine, permission: '/modules/inventory/dashboard' },
          { label: 'Inkooporders', path: '/voorraad/inkooporders', icon: faFileInvoice, permission: '/frontend/modules/inventory/purchaseorder' },
          { label: 'Mutaties', path: '/voorraad/mutaties', icon: faExchange, permission: '/frontend/modules/inventory/mutations' },
          { label: 'Tracking', path: '/voorraad/tracking', icon: faTruckMoving, permission: '/frontend/modules/inventory/tracking' },
        ],
      },
      {
        label: 'Administratie',
        icon: faReceipt,
        permission: '/frontend/modules/administratie',
        links: [
          { label: 'Werkbonnen', path: '/administratie/werkbonnen', icon: faBallotCheck, permission: '/frontend/modules/administratie/werkbonnen' },
          { label: 'Urenregistratie', path: '/administratie/urenregistratie', icon: faBusinessTime, permission: '/frontend/modules/administratie/urenregistratie', keywords: ['overuren'] },
        ],
      },
      {
        label: 'Tools',
        icon: faScrewdriverWrench,
        permission: '/frontend/modules/tools',
        links: [
          { label: 'Belkosten2Exact', path: '/tools/belkosten2exact', icon: faFileExport, permission: '/frontend/modules/tools/belkosten2exact', keywords: ['vialer'] },
          { label: 'Gpon Tool', path: '/tools/gponpreprocessor', icon: faSquarePlus, permission: '/frontend/modules/tools/gponpreprocessor', keywords: ['modem'] },
          { label: 'Zip Check Logs', path: '/tools/zipchecklogs', icon: faRectangleList, permission: '/modules/zip-check/logs', keywords: ['zipcheck', 'postcode'] },
        ],
      },
    ],
  },
  {
    label: 'Beheer',
    items: [
      {
        label: 'Koppelingen',
        icon: faLink,
        permission: '/frontend/modules/instellingen',
        links: [
          { label: 'KLIC / Kadaster', path: '/settings/klic', icon: faLandmark, permission: '/frontend/modules/instellingen/klic' },
          { label: 'Zoho CRM', path: '/settings/zoho', icon: faBuilding, permission: '/frontend/modules/instellingen/zoho' },
          { label: 'BGT Dataset', path: '/settings/bgt', icon: faLayerGroup, permission: '/modules/bgt' },
        ],
      },
      {
        label: 'Instellingen',
        icon: faSliders,
        permission: '/frontend/modules/instellingen',
        links: [
          { label: 'E-mailsjablonen', path: '/settings/emailsjablonen', icon: faEnvelopeOpenText, permission: '/frontend/modules/instellingen/emailsjablonen' },
          { label: 'Barcode Scanner', path: '/settings/scanner', icon: faScannerGun, permission: '/frontend/modules/instellingen/scanner' },
          { label: 'Outer VLANs', path: '/settings/outervlan', icon: faTags, permission: '/frontend/modules/instellingen/outervlan' },
          { label: 'Plaatsnamen', path: '/settings/plaatsnamen', icon: faCity, permission: '/frontend/modules/instellingen/plaatsnamen' },
          { label: 'Zipcheck-sites', path: '/settings/zipcheck-sites', icon: faGlobe, permission: '/modules/zip-check/sites' },
        ],
      },
      {
        label: 'Toegang',
        icon: faUserLock,
        permission: '/frontend/modules/instellingen',
        links: [
          { label: 'Gebruikers', path: '/settings/toegang/gebruikers', icon: faUser, permission: '/frontend/modules/instellingen/gebruikers', keywords: ['rechten', 'permissies'] },
          { label: 'Groepen', path: '/settings/toegang/groepen', icon: faUserGroup, permission: '/frontend/modules/instellingen/groepen', keywords: ['rechten', 'permissies'] },
          { label: 'Rate-Limit Whitelist', path: '/settings/rate-limit-whitelist', icon: faListCheck, permission: '/modules/systeem/rate-limit-whitelist' },
          { label: 'Autologin Tokens', path: '/settings/kiosk', icon: faKey, permission: '/modules/systeem/kiosk', keywords: ['kiosk'] },
        ],
      },
    ],
  },
]

export function isGroup(item: MenuLink | MenuGroup): item is MenuGroup {
  return 'links' in item
}

/** Every link with its group, in menu order. */
export function menuLinks(menu: MenuSection[]): MenuHit[] {
  return menu.flatMap(section => section.items.flatMap(item =>
    isGroup(item) ? item.links.map(link => ({ link, group: item })) : [{ link: item }],
  ))
}

/** The menu a user may see: links failing their own or their group's node go, then emptied groups and sections. */
export function visibleMenu(menu: MenuSection[], canRead: (node: string) => boolean): MenuSection[] {
  const allowed = (node?: string) => !node || canRead(node)

  return menu
    .map(section => ({
      ...section,
      items: section.items.flatMap((item): Array<MenuLink | MenuGroup> => {
        if (!allowed(item.permission))
          return []
        if (!isGroup(item))
          return [item]
        const links = item.links.filter(link => allowed(link.permission))
        return links.length ? [{ ...item, links }] : []
      }),
    }))
    .filter(section => section.items.length > 0)
}

/** The link a page belongs to: the longest path equal to the pathname or a parent of it. '/' only matches itself. */
export function findActiveLink(menu: MenuSection[], pathname: string): MenuHit | undefined {
  let best: MenuHit | undefined
  for (const hit of menuLinks(menu)) {
    const { path } = hit.link
    const matches = path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`)
    if (matches && (!best || path.length > best.link.path.length))
      best = hit
  }
  return best
}

/** Links whose label, group label or keywords contain every word of the query; labels starting with it first. */
export function searchMenu(menu: MenuSection[], query: string): MenuHit[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length)
    return []

  const hits = menuLinks(menu).filter(({ link, group }) => {
    const text = [link.label, group?.label, ...(link.keywords ?? [])].join(' ').toLowerCase()
    return words.every(word => text.includes(word))
  })
  const startsWithQuery = ({ link }: MenuHit) => link.label.toLowerCase().startsWith(words[0])
  return hits.sort((a, b) => Number(startsWithQuery(b)) - Number(startsWithQuery(a)))
}
